import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { getTenantSlugFromHostname } from '../utils/subdomain';

const Login = () => {
  const { user, loadUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const tenantSlug = getTenantSlugFromHostname();

  // 'credentials' or '2fa'
  const [step, setStep] = useState('credentials'); 
  
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [twoFaCode, setTwoFaCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [tempUserId, setTempUserId] = useState(null); 
  
  const { email, password } = formData;
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 1️⃣ UPDATE: Redirect if already logged in (e.g., Refresh)
  useEffect(() => {
    if (user) {
        // Redirect BOTH Admin and SuperAdmin to the Admin Portal
        if (user.role === 'admin' || user.role === 'superadmin') {
            navigate('/admin');
        } else {
            navigate('/dashboard');
        }
    }
  }, [user, navigate]);

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  // 🔄 SHARED LOGIN SUCCESS HANDLER
  const handleLoginSuccess = async (data) => {
      // 1. Save Token
      localStorage.setItem('token', data.token);
      api.defaults.headers.common['x-auth-token'] = data.token;
      
      // 2. Update Context
      await loadUser();

      // 3. UPDATE: Redirect based on Role (The Fix)
      // Check if user object exists and verify role
      const role = data.user ? data.user.role : null;
      
      if (role === 'admin' || role === 'superadmin') {
          navigate('/admin');
      } else {
          navigate('/dashboard');
      }
  };

  // STEP 1: CHECK PASSWORD & TRUSTED DEVICE
  const onSubmitCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const storedDeviceToken = localStorage.getItem('deviceToken');

      const res = await api.post('/auth/login-init', {
          email,
          password,
          deviceToken: storedDeviceToken,
          ...(tenantSlug ? { tenantSlug } : {})
      });

      setLoading(false);

      if (res.data.status === 'LOGIN_SUCCESS') {
          // ✅ TRUSTED! Login immediately
          await handleLoginSuccess(res.data);
      } else if (res.data.status === '2FA_REQUIRED') {
          // ⚠️ UNTRUSTED: Go to Step 2
          setTempUserId(res.data.userId);
          setStep('2fa');
      }

    } catch (err) {
      setLoading(false);

      // 👇 NEW: Check for Suspension Flag
      if (err.response?.data?.msg === 'ACCOUNT_SUSPENDED') {
          navigate('/account-suspended');
          return;
      }

      setError(err.response?.data?.msg || 'Login failed');
    }
  };

  // STEP 2: VERIFY EMAIL 2FA CODE & TRUST DEVICE
  const onSubmitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await api.post('/auth/login-verify', {
          userId: tempUserId,
          code: twoFaCode,
          trustDevice: trustDevice,
          ...(tenantSlug ? { tenantSlug } : {})
      });

      // Save new device token if requested
      if (res.data.deviceToken) {
          localStorage.setItem('deviceToken', res.data.deviceToken);
      }

      // ✅ SUCCESS! Login
      await handleLoginSuccess(res.data);

    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.msg || 'Invalid Code');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.formBox}>
        <h2 style={{ textAlign: 'center', marginBottom: '20px', color: '#2c3e50' }}>
            {step === 'credentials' ? 'Login' : 'Security Check'}
        </h2>
        
        {error && <div style={styles.error}>{error}</div>}
        
        {step === 'credentials' ? (
            <form onSubmit={onSubmitCredentials}>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Email Address</label>
                    <input type="email" name="email" value={email} onChange={onChange} required style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Password</label>
                    <div style={{position: 'relative'}}>
                        <input 
                            type={showPassword ? "text" : "password"} 
                            name="password" 
                            value={password} 
                            onChange={onChange} 
                            required 
                            style={styles.input} 
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} style={styles.eyeBtn}>
                            {showPassword ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22"/></svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            )}
                        </button>
                    </div>
                </div>
                
                <div style={{textAlign:'right', marginBottom:'15px'}}>
                    <Link to="/forgot-password" style={{fontSize:'0.9rem', color:'#3498db', textDecoration:'none'}}>Forgot Password?</Link>
                </div>

                <button type="submit" style={styles.button} disabled={loading}>{loading ? 'Checking...' : 'Login'}</button>
            </form>
        ) : (
            <form onSubmit={onSubmitCode}>
                <p style={{textAlign:'center', marginBottom:'20px', color:'#555'}}>
                    Enter the code sent to your email.
                </p>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Email Code</label>
                    <input 
                        type="text" 
                        value={twoFaCode} 
                        onChange={(e) => setTwoFaCode(e.target.value)} 
                        placeholder="123456" 
                        style={{...styles.input, textAlign:'center', letterSpacing:'5px', fontSize:'1.2rem'}} 
                        required 
                    />
                </div>
                
                <div style={{marginBottom:'20px', display:'flex', alignItems:'center', gap:'10px', justifyContent:'center'}}>
                    <input 
                        type="checkbox" 
                        id="trustDevice" 
                        checked={trustDevice} 
                        onChange={(e) => setTrustDevice(e.target.checked)} 
                        style={{width:'20px', height:'20px', cursor:'pointer'}}
                    />
                    <label htmlFor="trustDevice" style={{fontSize:'0.95rem', cursor:'pointer', userSelect:'none'}}>
                        Trust this device for 30 days
                    </label>
                </div>

                <button type="submit" style={styles.button} disabled={loading}>{loading ? 'Verifying...' : 'Verify & Login'}</button>
                <button type="button" onClick={() => setStep('credentials')} style={{...styles.button, backgroundColor:'#bdc3c7', marginTop:'10px'}}>Back</button>
            </form>
        )}

        {step === 'credentials' && (
            <p style={{ marginTop: '20px', textAlign: 'center', fontSize:'0.9rem' }}>
                Don't have an account? <Link to="/register" style={{color: '#3498db', fontWeight:'bold', textDecoration:'none'}}>Register</Link>
            </p>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f4f6f8', padding: '20px' },
  formBox: { width: '400px', padding: '40px', backgroundColor: 'white', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', borderRadius: '12px' },
  inputGroup: { marginBottom: '15px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '0.9rem', fontWeight: '600', color: '#34495e' },
  input: { width: '100%', padding: '12px', border: '1px solid #dfe6e9', borderRadius: '6px', fontSize: '1rem', boxSizing: 'border-box', outline: 'none', transition: 'border 0.2s' },
  button: { width: '100%', padding: '14px', backgroundColor: '#2ecc71', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', marginTop: '10px' },
  error: { backgroundColor: '#ffebee', color: '#c62828', padding: '12px', marginBottom: '20px', borderRadius: '6px', textAlign: 'center', fontSize: '0.9rem' },
  eyeBtn: { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display:'flex', alignItems:'center' }
};

export default Login;